import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDistributorDashboard, getDistributorFundRequests, createDistributorFundRequest, uploadFile, getAgencyLedgerSummary, createAgencyRequest, getAgencyRequests, getDistributorLedger, getAgencyFiles } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Wallet, ArrowUp, ArrowDown, Buildings, PaperPlaneTilt, Clock, CheckCircle, XCircle, Image, Warning, UploadSimple, Receipt, Plus, Storefront, CaretDown, CaretUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DistributorSummaryCards } from '../components/distributor/DistributorSummaryCards';
import { DistributorFundRequestDialog } from '../components/distributor/DistributorFundRequestDialog';

const LOW_BALANCE_THRESHOLD = 5000;
const API_BASE = window.location.origin;

const PAYMENT_MODES = ['CDM', 'Branch Deposit', 'Online Transfer', 'QR Code'];

const BANK_LIST = [
  'Union Bank of India',
  'HDFC ***5678',
  'PNB 014',
  'RBL Bank',
  'Bandhan Bank',
  'Bank of Maharashtra',
  'IDBI',
];

const ONLINE_TRANSFER_ACCOUNTS = [
  'HDFC ****8043',
  'HDFC 7046',
  'HDFC 3497',
];

export default function DistributorDashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [fundRequests, setFundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Agency Ledger & Request
  const [agencySummary, setAgencySummary] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [agencyLedger, setAgencyLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [agencyRequests, setAgencyRequests] = useState([]);
  const [agencyDialogOpen, setAgencyDialogOpen] = useState(false);
  const [agencyForm, setAgencyForm] = useState({ agency_name: '', oil_company: '', rate: '', convenience_fee: '', notes: '' });
  const [agencyFilesList, setAgencyFilesList] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    amount: '',
    payment_mode: '',
    bank_name: '',
    account_info: '',
    reference_number: '',
    deposit_date: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
    screenshot_url: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, reqRes, summaryRes, agReqRes, afRes] = await Promise.all([
        getDistributorDashboard().catch(err => ({ data: { agencies: [], total_balance: 0, total_debit: 0, total_credit: 0, recent_transactions: [], pending_fund_requests: 0 }, _error: err })),
        getDistributorFundRequests().catch(() => ({ data: [] })),
        getAgencyLedgerSummary().catch(() => ({ data: [] })),
        getAgencyRequests().catch(() => ({ data: [] })),
        getAgencyFiles({}).catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setFundRequests(reqRes.data || []);
      setAgencySummary(summaryRes.data || []);
      setAgencyRequests(agReqRes.data || []);
      setAgencyFilesList(afRes.data || []);
      if (dashRes._error) {
        toast.error('Dashboard data partially load hua. Refresh karo.');
      }
    } catch (err) {
      toast.error('Dashboard load nahi hua. Internet check karo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({
      amount: '', payment_mode: '', bank_name: '', account_info: '',
      reference_number: '', deposit_date: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
      screenshot_url: '', notes: '',
    });
  };

  const handleSubmitFundRequest = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) { toast.error('Amount daalna zaroori hai'); return; }
    if (!formData.payment_mode) { toast.error('Payment mode select karein'); return; }
    if ((formData.payment_mode === 'CDM' || formData.payment_mode === 'Branch Deposit') && !formData.bank_name) {
      toast.error('Bank select karein'); return;
    }
    if (formData.payment_mode === 'Online Transfer' && !formData.account_info) {
      toast.error('Account select karein'); return;
    }
    setSubmitting(true);
    try {
      await createDistributorFundRequest({
        amount: parseFloat(formData.amount),
        payment_mode: formData.payment_mode,
        bank_name: formData.bank_name,
        account_info: formData.account_info,
        reference_number: formData.reference_number,
        deposit_date: formData.deposit_date,
        screenshot_url: formData.screenshot_url,
        notes: formData.notes,
      });
      toast.success('Fund request submitted!');
      resetForm();
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request fail hua');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadFile(fd);
      setFormData(p => ({ ...p, screenshot_url: res.data.url }));
      toast.success('Receipt uploaded');
    } catch (err) {
      toast.error('Upload fail');
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (val) => {
    if (val == null) return '₹0';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(val);
  };

  // Agency-wise ledger fetch
  const fetchAgencyLedger = async (agencyId) => {
    if (!agencyId) { setAgencyLedger([]); return; }
    setLedgerLoading(true);
    try {
      const res = await getDistributorLedger(agencyId);
      setAgencyLedger(res.data?.ledger || []);
    } catch (err) { toast.error('Ledger load fail'); }
    finally { setLedgerLoading(false); }
  };

  const handleAgencySelect = (aid) => {
    setSelectedAgencyId(aid);
    fetchAgencyLedger(aid);
  };

  const handleAgencyRequest = async () => {
    if (!agencyForm.agency_name.trim()) { toast.error('Agency name zaroori hai'); return; }
    setSubmitting(true);
    try {
      await createAgencyRequest(agencyForm);
      toast.success('Agency request submitted! Admin approve karega.');
      setAgencyForm({ agency_name: '', oil_company: '', rate: '', convenience_fee: '', notes: '' });
      setAgencyDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Request fail'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="p-6 flex justify-center items-center min-h-[50vh]"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;
  }

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle size={16} weight="fill" className="text-green-600" />;
    if (status === 'rejected') return <XCircle size={16} weight="fill" className="text-red-600" />;
    return <Clock size={16} weight="fill" className="text-amber-500" />;
  };

  const paymentModeLabel = (mode) => {
    if (!mode) return '-';
    return mode;
  };

  return (
    <div className="space-y-5" data-testid="distributor-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="dist-dashboard-title">
            {user?.agency_names?.length > 0 ? user.agency_names.join(', ') : 'Dashboard'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {user?.agency_names?.length > 0 ? <>Owner: <b>{user?.name}</b></> : <>Welcome, {user?.name}</>}
          </p>
        </div>
        <DistributorFundRequestDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          resetForm={resetForm}
          formData={formData}
          setFormData={setFormData}
          PAYMENT_MODES={PAYMENT_MODES}
          BANK_LIST={BANK_LIST}
          ONLINE_TRANSFER_ACCOUNTS={ONLINE_TRANSFER_ACCOUNTS}
          handleSubmitFundRequest={handleSubmitFundRequest}
          submitting={submitting}
          handleScreenshot={handleScreenshot}
          uploading={uploading}
        />
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="dist-tabs">
        {[
          { key: 'overview', label: 'Overview', icon: <Wallet size={16} /> },
          { key: 'agencies', label: `Agencies (${agencySummary.length})`, icon: <Storefront size={16} /> },
          { key: 'files', label: `Files (${agencyFilesList.length})`, icon: <PaperPlaneTilt size={16} /> },
          { key: 'fund_requests', label: 'Fund Requests', icon: <Receipt size={16} /> },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs sm:text-sm font-medium transition ${
              activeTab === tab.key ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === 'overview' && (
        <>
          {/* Low Balance Warning */}
          {dashboard && dashboard.total_balance < LOW_BALANCE_THRESHOLD && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row items-start gap-3" data-testid="low-balance-warning">
          <Warning size={22} weight="fill" className="text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-red-800 text-sm">Low Wallet Balance!</p>
            <p className="text-xs sm:text-sm text-red-600 mt-0.5">
              Balance {formatCurrency(dashboard.total_balance)} hai. Please fund add karein.
            </p>
          </div>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 self-start sm:self-auto" onClick={() => setDialogOpen(true)} data-testid="low-balance-fund-btn">
            Fund Add
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <DistributorSummaryCards dashboard={dashboard} formatCurrency={formatCurrency} />

      {/* Agencies */}
      {dashboard?.agencies?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5">
          <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2"><Buildings size={20} /> Assigned Agencies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            {dashboard.agencies.map((ag, idx) => (
              <div key={`${ag.id || 'agency'}-${ag.name || ''}-${idx}`} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                <p className="font-medium text-gray-900 text-sm">{ag.name}</p>
                <p className="text-xs text-gray-500">Rate: {formatCurrency(ag.rate)} | Fee: {formatCurrency(ag.convenience_fee)}/cyl</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5">
        <h2 className="text-base sm:text-lg font-semibold mb-3">Recent Transactions</h2>
        {dashboard?.recent_transactions?.length > 0 ? (
          <>
            <div className="sm:hidden space-y-2">
              {dashboard.recent_transactions.map((txn, i) => (
                <div key={`${txn.agency_name || 'agency'}-${i}`} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{txn.agency_name}</p>
                      <p className="text-xs text-gray-400">{txn.date} · {txn.category}</p>
                    </div>
                    <span className={`text-sm font-semibold ${txn.type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                      {txn.type === 'debit' ? '-' : '+'}{formatCurrency(txn.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>TXN ID</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboard.recent_transactions.map((txn, i) => (
                    <TableRow key={txn.transaction_id || `txn-${i}`}>
                      <TableCell className="text-sm">{txn.date}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">{txn.transaction_id || '-'}</TableCell>
                      <TableCell className="text-sm font-medium">{txn.agency_name}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded ${txn.type === 'debit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {txn.type === 'debit' ? 'DEBIT' : 'CREDIT'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{txn.category}</TableCell>
                      <TableCell className={`text-right font-medium ${txn.type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                        {txn.type === 'debit' ? '-' : '+'}{formatCurrency(txn.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <p className="text-gray-400 text-center py-6 text-sm">No transactions yet</p>
        )}
      </div>
        </>
      )}

      {/* ═══ AGENCIES TAB ═══ */}
      {activeTab === 'agencies' && (
        <div className="space-y-4">
          {/* Add Agency Request Button */}
          <div className="flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-semibold">Agency-wise Ledger</h2>
            <Dialog open={agencyDialogOpen} onOpenChange={setAgencyDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="add-agency-request-btn">
                  <Plus size={14} /> New Agency Request
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-md">
                <DialogHeader><DialogTitle>New Agency Add Request</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-sm font-medium">Agency Name <span className="text-red-500">*</span></Label>
                    <Input value={agencyForm.agency_name} onChange={e => setAgencyForm(p => ({...p, agency_name: e.target.value}))} placeholder="Agency ka naam" data-testid="agency-req-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Oil Company</Label>
                    <select value={agencyForm.oil_company} onChange={e => setAgencyForm(p => ({...p, oil_company: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" data-testid="agency-req-company">
                      <option value="">Select...</option>
                      <option value="IOCL">IOCL (Indane)</option>
                      <option value="BPCL">BPCL (Bharat Gas)</option>
                      <option value="HPCL">HPCL (HP Gas)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm font-medium">Rate (₹)</Label>
                      <Input type="number" step="0.01" value={agencyForm.rate} onChange={e => setAgencyForm(p => ({...p, rate: e.target.value}))} placeholder="0" data-testid="agency-req-rate" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Conv. Fee (₹)</Label>
                      <Input type="number" step="0.01" value={agencyForm.convenience_fee} onChange={e => setAgencyForm(p => ({...p, convenience_fee: e.target.value}))} placeholder="0" data-testid="agency-req-fee" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Notes</Label>
                    <Input value={agencyForm.notes} onChange={e => setAgencyForm(p => ({...p, notes: e.target.value}))} placeholder="Optional details..." data-testid="agency-req-notes" />
                  </div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleAgencyRequest} disabled={submitting} data-testid="agency-req-submit">
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Agency Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {agencySummary.map(ag => (
              <div
                key={ag.agency_id}
                onClick={() => handleAgencySelect(ag.agency_id === selectedAgencyId ? '' : ag.agency_id)}
                className={`p-3 sm:p-4 border rounded-lg cursor-pointer transition hover:shadow-md ${selectedAgencyId === ag.agency_id ? 'border-emerald-500 bg-emerald-50 shadow' : 'border-gray-200 bg-white'}`}
                data-testid={`agency-card-${ag.agency_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm truncate">{ag.agency_name}</h3>
                  {selectedAgencyId === ag.agency_id ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-gray-400">Credit</span><p className="font-bold text-green-600">{formatCurrency(ag.total_credit)}</p></div>
                  <div><span className="text-gray-400">Debit</span><p className="font-bold text-red-600">{formatCurrency(ag.total_debit)}</p></div>
                  <div><span className="text-gray-400">Balance</span><p className={`font-bold ${ag.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(ag.balance)}</p></div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{ag.transactions} transactions · Rate: ₹{ag.rate} · Fee: ₹{ag.convenience_fee}</p>
              </div>
            ))}
          </div>

          {/* Agency Ledger Detail */}
          {selectedAgencyId && (
            <div className="bg-white border rounded-lg p-3 sm:p-4">
              <h3 className="text-sm font-semibold mb-2">
                Ledger: {agencySummary.find(a => a.agency_id === selectedAgencyId)?.agency_name}
              </h3>
              {ledgerLoading ? (
                <div className="flex justify-center py-6"><div className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>
              ) : agencyLedger.length > 0 ? (
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 text-xs">
                        <TableHead className="py-1 px-2">Date</TableHead>
                        <TableHead className="py-1 px-2">TXN ID</TableHead>
                        <TableHead className="py-1 px-2">Category</TableHead>
                        <TableHead className="py-1 px-2">Description</TableHead>
                        <TableHead className="py-1 px-2 text-right text-green-700">Credit</TableHead>
                        <TableHead className="py-1 px-2 text-right text-red-700">Debit</TableHead>
                        <TableHead className="py-1 px-2 text-right font-bold">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agencyLedger.map((e, i) => (
                        <TableRow key={e.transaction_id || `ledger-${i}`} className={e.type === 'credit' ? 'bg-green-50/30' : ''}>
                          <TableCell className="text-xs py-1 px-2 whitespace-nowrap">{e.date}</TableCell>
                          <TableCell className="text-xs py-1 px-2 font-mono text-gray-500">{e.transaction_id || '-'}</TableCell>
                          <TableCell className="text-xs py-1 px-2">{e.category}</TableCell>
                          <TableCell className="text-xs py-1 px-2 max-w-[200px] truncate">{e.description}</TableCell>
                          <TableCell className="text-xs py-1 px-2 text-right text-green-700">{e.type === 'credit' ? formatCurrency(e.amount) : ''}</TableCell>
                          <TableCell className="text-xs py-1 px-2 text-right text-red-700">{e.type === 'debit' ? formatCurrency(e.amount) : ''}</TableCell>
                          <TableCell className="text-xs py-1 px-2 text-right font-bold">{formatCurrency(e.balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4 text-sm">Is agency ka koi transaction nahi hai</p>
              )}
            </div>
          )}

          {/* Pending Agency Requests */}
          {agencyRequests.length > 0 && (
            <div className="bg-white border rounded-lg p-3 sm:p-4">
              <h3 className="text-sm font-semibold mb-2">Agency Requests</h3>
              <div className="space-y-2">
                {agencyRequests.map(r => (
                  <div key={r.id} className={`flex items-center justify-between p-2 rounded border text-xs ${r.status === 'pending' ? 'bg-amber-50 border-amber-200' : r.status === 'approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div>
                      <span className="font-semibold">{r.agency_name}</span>
                      <span className="text-gray-400 ml-2">{r.oil_company}</span>
                      {r.notes && <span className="text-gray-400 ml-2">· {r.notes}</span>}
                    </div>
                    <span className={`px-2 py-0.5 rounded font-bold ${r.status === 'pending' ? 'bg-amber-100 text-amber-700' : r.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ FUND REQUESTS TAB ═══ */}
      {/* Files Tab */}
      {activeTab === 'files' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5" data-testid="dist-files-tab">
          <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
            <PaperPlaneTilt size={20} className="text-emerald-600" /> Agency Files
          </h2>
          {agencyFilesList.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Koi file abhi nahi hai</p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Oil Co.</TableHead>
                    <TableHead className="text-right">Entries</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Bookings</TableHead>
                    <TableHead className="text-right">Debited</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agencyFilesList.map(f => (
                    <TableRow key={f.id} data-testid={`dist-af-${f.id}`}>
                      <TableCell className="text-sm">{f.entry_date}</TableCell>
                      <TableCell className="font-medium text-sm">{f.agency_name}</TableCell>
                      <TableCell><span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{f.oil_company}</span></TableCell>
                      <TableCell className="text-right text-sm">{f.entry_count}</TableCell>
                      <TableCell className="text-sm">{f.assigned_to_name || '-'}</TableCell>
                      <TableCell>
                        {f.status === 'completed' ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Done</span>
                        ) : f.status === 'assigned' ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Processing</span>
                        ) : (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Pending</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">{f.total_bookings_count || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-red-600">
                        {f.total_amount_debited > 0 ? `₹${f.total_amount_debited.toLocaleString('en-IN')}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-2 text-xs text-gray-500 flex gap-4">
                <span>Total: {agencyFilesList.length} files</span>
                <span>Entries: {agencyFilesList.reduce((s, f) => s + (f.entry_count || 0), 0)}</span>
                <span>Bookings: {agencyFilesList.reduce((s, f) => s + (f.total_bookings_count || 0), 0)}</span>
                <span className="text-red-600 font-medium">Debited: ₹{agencyFilesList.reduce((s, f) => s + (f.total_amount_debited || 0), 0).toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'fund_requests' && (
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base sm:text-lg font-semibold">Fund Requests</h2>
          <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)} className="gap-1 text-xs">
            <PaperPlaneTilt size={14} /> New Request
          </Button>
        </div>
        {fundRequests.length > 0 ? (
          <>
            <div className="sm:hidden space-y-2">
              {fundRequests.map(req => (
                <div key={req.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-medium text-emerald-600">{formatCurrency(req.amount)}</span>
                    <span className="flex items-center gap-1">
                      {statusIcon(req.status)}
                      <span className={`text-xs font-medium ${req.status === 'approved' ? 'text-green-600' : req.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>
                        {req.status.toUpperCase()}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {req.payment_mode && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{req.payment_mode}</span>}
                    {req.bank_name && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{req.bank_name}</span>}
                    {req.account_info && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{req.account_info}</span>}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(req.created_at)}</p>
                  {req.reference_number && <p className="text-xs text-gray-500 mt-0.5">Ref: {req.reference_number}</p>}
                </div>
              ))}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>TXN ID</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Bank / Account</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Ref No.</TableHead>
                    <TableHead>Receipt</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fundRequests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell className="font-mono text-xs text-gray-600">{req.transaction_id || '-'}</TableCell>
                      <TableCell className="text-sm">{req.deposit_date || formatDate(req.created_at)}</TableCell>
                      <TableCell><span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{paymentModeLabel(req.payment_mode)}</span></TableCell>
                      <TableCell className="text-sm">{req.bank_name || req.account_info || '-'}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">{formatCurrency(req.amount)}</TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-[120px] truncate">{req.reference_number || '-'}</TableCell>
                      <TableCell>
                        {req.screenshot_url ? (
                          <a href={`${API_BASE}${req.screenshot_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1 text-xs">
                            <Image size={14} /> View
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          {statusIcon(req.status)}
                          <span className={`text-xs font-medium ${req.status === 'approved' ? 'text-green-600' : req.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>
                            {req.status.toUpperCase()}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <p className="text-gray-400 text-center py-6 text-sm">No fund requests yet</p>
        )}
      </div>
      )}
    </div>
  );
}
